/**
 * ZuweisenPanel — the "Zuweisen" card on the Kontrollbereich detail (right column).
 *
 * [Person | Aufgaben-Funktionsgruppe] toggle → a "{…} wählen …" dropdown → the NEW
 * "Frist (optional)" date field (Stefan §11 "mit Frist versehen" — added on top of the
 * prototype) → the salmon "Zuweisen" button. The assign action AND the Frist field are
 * gated on administration.kontrolle.rueckstand.zuweisen (the effective-permission hook);
 * the subtext states the required key. Zuweisen does NOT set the element to "bearbeitet".
 *
 * On success the detail + the stage list + the overview are invalidated.
 *
 * [Source: V4.0-inbox/cockpit-screenshots/image3,5,8,11.png] (the Zuweisen card)
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '../../../lib/cn';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { useAdminMembers, useFunktionsgruppen } from '../../administration/hooks';
import { KONTROLLE_ZUWEISEN_KEY } from '../constants';
import type { KontrolleStufe, ZuweisenInput } from '../types';

type Mode = 'person' | 'gruppe';

export function ZuweisenPanel({ itemId, stufe }: { itemId: string; stufe: KontrolleStufe }) {
  const qc = useQueryClient();
  const access = useEffectivePermissions();
  const canZuweisen = access.can(KONTROLLE_ZUWEISEN_KEY);

  const membersQuery = useAdminMembers();
  const groupsQuery = useFunktionsgruppen();
  const members = membersQuery.data?.members ?? [];
  const groups = groupsQuery.data?.funktionsgruppen ?? [];

  const [mode, setMode] = useState<Mode>('person');
  const [zielId, setZielId] = useState('');
  const [frist, setFrist] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const zuweisen = useMutation({
    mutationFn: () => {
      const body: ZuweisenInput = { typ: mode, ziel_id: zielId };
      if (frist) body.frist = frist;
      return apiClient.admin.kontrolleZuweisen(itemId, body);
    },
    onSuccess: () => {
      setDone(true);
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.kontrolleRueckstandDetail(itemId) });
      void qc.invalidateQueries({ queryKey: qk.kontrolleRueckstandStufe(stufe) });
      void qc.invalidateQueries({ queryKey: qk.kontrolleUebersicht() });
    },
    onError: (e: Error) => setError(e.message),
  });

  const selectLabel = mode === 'person' ? 'Person wählen' : 'Aufgaben-Funktionsgruppe wählen';
  const disabled = !canZuweisen;
  const canSubmit = canZuweisen && zielId !== '' && !zuweisen.isPending;

  function toggle(next: Mode) {
    setMode(next);
    setZielId('');
    setDone(false);
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-xs">
      <h3 className="text-base font-semibold">Zuweisen</h3>

      {/* [Person | Aufgaben-Funktionsgruppe] toggle */}
      <div role="radiogroup" aria-label="Zuweisen an" className="mt-4 grid grid-cols-2 gap-2">
        {(['person', 'gruppe'] as const).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => toggle(m)}
              className={cn(
                'h-9 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
                active
                  ? 'bg-foreground text-background'
                  : 'border border-border bg-card text-foreground hover:bg-secondary',
              )}
            >
              {m === 'person' ? 'Person' : 'Aufgaben-Funktionsgruppe'}
            </button>
          );
        })}
      </div>

      {/* "{…} wählen …" dropdown */}
      <label className="mt-3 block text-sm">
        <span className="sr-only">{selectLabel}</span>
        <select
          aria-label={selectLabel}
          disabled={disabled}
          value={zielId}
          onChange={(e) => {
            setZielId(e.target.value);
            setDone(false);
          }}
          className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm disabled:opacity-50"
        >
          <option value="">{selectLabel} …</option>
          {mode === 'person'
            ? members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.display_name ?? m.email}
                </option>
              ))
            : groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
        </select>
      </label>

      {/* Frist (optional) — the NEW field (Stefan §11), gated on the zuweisen permission. */}
      <label className="mt-3 block text-sm">
        <span className="mb-1 block text-muted-foreground">Frist (optional)</span>
        <input
          type="date"
          aria-label="Frist (optional)"
          disabled={disabled}
          value={frist}
          onChange={(e) => setFrist(e.target.value)}
          className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm disabled:opacity-50"
        />
      </label>

      {/* Salmon "Zuweisen" primary */}
      <button
        type="button"
        onClick={() => zuweisen.mutate()}
        disabled={!canSubmit}
        className={cn(
          'mt-4 h-9 w-full rounded-md bg-kontrolle-accent px-4 text-sm font-medium text-kontrolle-accent-foreground transition-colors hover:bg-kontrolle-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        )}
      >
        {zuweisen.isPending ? 'Wird zugewiesen …' : 'Zuweisen'}
      </button>

      {done && !error && (
        <p className="mt-2 text-xs text-status-success">Zugewiesen. Das Element bleibt im Kontrollbereich sichtbar.</p>
      )}
      {error != null && <p className="mt-2 text-xs text-status-danger">{error}</p>}

      <div className="mt-4 border-t border-hairline pt-3">
        <p className="text-xs text-muted-foreground">
          Erfordert administration.kontrolle.rueckstand.zuweisen
        </p>
      </div>
    </div>
  );
}
